import React, { useRef, useState, useEffect, useMemo } from "react";

import { Typography } from "@mui/material";

import SecurityTopics from "./components/SecurityTopics";
import styles from "./SecurityAwareness.module.scss";

import ExclusiveAccordionList from "@/components/ExclusiveAccordionList";
import HeroBlock from "@/pages/SecurityAwareness/components/HeroBlock";
import Password2Fa from "@/pages/SecurityAwareness/components/Password2Fa";
import Phishing from "@/pages/SecurityAwareness/components/Phishing";
import ConfidentialInfo from "@/pages/SecurityAwareness/components/ConfidentialInfo";
import PhysicalSecurity from "@/pages/SecurityAwareness/components/PhysicalSecurity";
import ThirdPartyLeakage from "@/pages/SecurityAwareness/components/ThirdPartyLeakage";
import MobileThreats from "@/pages/SecurityAwareness/components/MobileThreats";
import EmployeeResponsibility from "@/pages/SecurityAwareness/components/EmployeeResponsibility";
import { SecurityTopicEnum } from "@/pages/SecurityAwareness/types";
import { SCROLL_DELAY_500, TopicTitles, mapping } from "@/constants/common";
import { getAllUserTestResults } from "@/firebase";
import { auth } from "@/firebase";

interface TestResult {
  score_percent: number;
  completed_at: string;
  id: string;
}

const SecurityAwareness = () => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [testResults, setTestResults] = useState<Map<string, TestResult>>(
    new Map()
  );
  const accordionRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Load test results when component mounts
  useEffect(() => {
    const loadTestResults = async () => {
      try {
        const user = auth.currentUser;
        if (user && user.uid) {
          const results = await getAllUserTestResults(user.uid);
          setTestResults(results as Map<string, TestResult>);
        }
      } catch (error) {
        console.error("Failed to load test results:", error);
        setTestResults(new Map());
      }
    };

    // Wait for auth to be ready
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user && user.uid) {
        loadTestResults();
      } else {
        setTestResults(new Map());
      }
    });

    return () => unsubscribe();
  }, []);

  // Define topics with their enum values
  const topics = [
    SecurityTopicEnum.Phishing,
    SecurityTopicEnum.Password2Fa,
    SecurityTopicEnum.ConfidentialInfo,
    SecurityTopicEnum.PhysicalSecurity,
    SecurityTopicEnum.ThirdPartyLeakage,
    SecurityTopicEnum.MobileThreats,
    SecurityTopicEnum.EmployeeResponsibility,
  ];

  const handleTopicClick = (topicNumber: number) => {
    // topicNumber starts from 1, but index starts from 0
    const index = topicNumber - 1;
    if (index >= 0 && index < topics.length) {
      setExpandedIndex(index);

      setTimeout(() => {
        accordionRefs.current[index]?.scrollIntoView({ behavior: "smooth" });
      }, SCROLL_DELAY_500);
    }
  };

  // Create items with isPassed flag based on test results
  const items = useMemo(() => {
    return topics.map((topic, index) => {
      const testCode = mapping[topic];
      const isPassed = testResults.has(testCode);

      const contentComponents = [
        <Phishing
          key={SecurityTopicEnum.Phishing}
          handleNextTopic={() => handleTopicClick(2)}
          isPassed={isPassed}
          setTestResults={setTestResults}
        />,
        <Password2Fa
          key={SecurityTopicEnum.Password2Fa}
          handleNextTopic={() => handleTopicClick(3)}
          isPassed={isPassed}
          setTestResults={setTestResults}
        />,
        <ConfidentialInfo
          key={SecurityTopicEnum.ConfidentialInfo}
          handleNextTopic={() => handleTopicClick(4)}
          isPassed={isPassed}
          setTestResults={setTestResults}
        />,
        <PhysicalSecurity
          key={SecurityTopicEnum.PhysicalSecurity}
          handleNextTopic={() => handleTopicClick(5)}
          isPassed={isPassed}
          setTestResults={setTestResults}
        />,
        <ThirdPartyLeakage
          key={SecurityTopicEnum.ThirdPartyLeakage}
          handleNextTopic={() => handleTopicClick(6)}
          isPassed={isPassed}
          setTestResults={setTestResults}
        />,
        <MobileThreats
          key={SecurityTopicEnum.MobileThreats}
          handleNextTopic={() => handleTopicClick(7)}
          isPassed={isPassed}
          setTestResults={setTestResults}
        />,
        <EmployeeResponsibility
          key={SecurityTopicEnum.EmployeeResponsibility}
          isPassed={isPassed}
          setTestResults={setTestResults}
        />,
      ];

      return {
        title: `${index + 1}. ${TopicTitles[topic]}`,
        content: contentComponents[index],
        isPassed,
      };
    });
  }, [testResults]);

  return (
    <div className={styles.wrapper}>
      <HeroBlock
        onGetStartedClick={() => {
          setExpandedIndex(0);
          setTimeout(() => {
            accordionRefs.current[0]?.scrollIntoView({ behavior: "smooth" });
          }, 350);
        }}
      />
      <SecurityTopics onTopicClick={handleTopicClick} />
      <Typography
        variant="h3"
        marginBottom={3}
        fontSize="40px"
        fontWeight={700}
      >
        Core Topics in Information Security
      </Typography>

      <ExclusiveAccordionList
        items={items}
        expandedIndex={expandedIndex}
        onChangeExpanded={setExpandedIndex}
        accordionRefs={accordionRefs}
      />
    </div>
  );
};

export default SecurityAwareness;
